import { useState } from 'react';

import { EditorState, convertToRaw } from 'draft-js';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  convertPlaceholders,
  placeholdersToBrackets,
} from '@components/text-editor/plugin-placeholder';
import { draftToHtml, isEditorEmpty, makeEditorState } from '@utils/draftjs';

import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal } from 'features/modals';
import { useStore } from 'models/Provider';

import Form from './Form';

function MessageApplicantsModal({ applicants, applicant, closeModal }) {
  const { current, t } = useStore();
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState({
    message: EditorState.createEmpty(),
    id: -1,
  });
  const templates = current.messageTemplates;

  function onTemplateSelect(id) {
    const select = templates.find((t) => t.id === id);
    if (select) {
      const { contentHtml, contentTxt } = select;
      const editorState = makeEditorState(contentHtml || contentTxt);

      setSelected((old) => ({
        message: convertPlaceholders(editorState, t),
        id,
      }));
    }
  }

  function setEditorState(editorState) {
    setSelected((old) => ({
      ...old,
      message: convertPlaceholders(editorState, t),
    }));
  }

  async function onSubmit() {
    const { message } = selected;
    const job = applicant ? applicant.job : applicants[0].job;
    let success;
    setSubmitting(true);

    const rawData = convertToRaw(message.getCurrentContent());
    const converted = placeholdersToBrackets(rawData);

    const emailData = {
      message: draftToHtml(converted),
    };
    if (applicant) {
      success = await applicant.sendMessage(message);
    } else {
      success = await job.bulkMessageApplicants(applicants, emailData);
    }

    if (success) {
      closeModal();
    } else {
      setSubmitting(false);
    }
  }

  const disableButtons = submitting || isEditorEmpty(selected.message);
  return (
    <Modal>
      <Modal.Header text={t('job.bulkmessage.title')} />
      {submitting && <Loading />}
      {applicant && (
        <Text
          text={
            <>
              {t('job.bulkmessage.confirm.single')} <strong>{applicant.name}</strong>?
            </>
          }
        />
      )}

      {applicants && (
        <Stack space={3}>
          <Text text={t('job.bulkmessage.confirm.multi')} />
          <ShowMore
            max={applicants.length}
            count={applicants.length > 15 ? 10 : applicants.length}
            inc={1000}
            render={({ count }) => (
              <div>
                {applicants.slice(0, count).map((a, i) => (
                  <Name key={i}>
                    <span>{a.name}</span>
                  </Name>
                ))}
              </div>
            )}
          />
        </Stack>
      )}
      <Separator />
      <Form
        disabled={submitting}
        templates={templates}
        selected={selected}
        onSelect={onTemplateSelect}
        setEditorState={setEditorState}
      />
      <Modal.Actions>
        <Button
          isDisabled={submitting}
          variant="secondary"
          onClick={closeModal}
          text={t('job.bulkmessage.button.cancel')}
        />
        <Button
          isDisabled={disableButtons}
          onClick={onSubmit}
          text={t('job.bulkmessage.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default observer(MessageApplicantsModal);

const Name = styled.div`
  color: ${(p) => p.theme.textColor};
  > span:nth-child(1) {
    font-weight: bold;
  }
  > span:nth-child(2) {
    font-style: italic;
    margin-left: 5px;
  }
`;
